import { Box, Typography } from '@mui/material'
import { Table } from 'antd'
import dayjs from 'dayjs'
import React, { forwardRef, RefObject } from 'react'

import CircularLoading from '../../../../../components/CircularProgress'
import { DATE_FORMAT_INTERNATIONAL } from '../../../../../constants/global'
import { IMerchant } from '../../../../../types/merchant'
import {
  ignoreDateTimezone,
  mergeObjects,
  numberFormat,
} from '../../../../../utils'
import { ICostDailyReport, IMerchantDailyReport } from '../index'
import { IFilterMapped } from './DailySalesReportFilter'
import { DailySalesReportOrderTableModal } from './DailySalesReportOrderTableModal'
import { useUser } from '../../../../../hooks/useUser'

export interface IDailySalesTableProps {
  merchantDailyReports?: IMerchantDailyReport[]
  costDailyReport?: ICostDailyReport[]
  isLoading: boolean
  merchant?: IMerchant
  filter: IFilterMapped
  tableRef?: RefObject<any>
}

export interface IReport extends IMerchantDailyReport {
  costAmountTotal: number
}

const DailyTelesalesReportTable = forwardRef<
  HTMLTableElement,
  IDailySalesTableProps
>((props: IDailySalesTableProps, ref) => {
  const { merchantDailyReports, merchant, filter, tableRef, isLoading } = props
  const { user } = useUser()
  const [isOpen, setIsOpen] = React.useState(false)
  const [date, setDate] = React.useState({ startDate: '', endDate: '' })
  const { commissionType = '', expense = 1, calTax = 0 } = filter
  const commissionTypeName = commissionType === 'all' ? '' : commissionType

  const merchantObject = merchantDailyReports?.reduce(
    (acc: any, merchantDailyReportItem: IMerchantDailyReport) => {
      const date = dayjs(
        ignoreDateTimezone(merchantDailyReportItem.date),
      ).format(DATE_FORMAT_INTERNATIONAL)
      acc[date] = merchantDailyReportItem
      return acc
    },
    {},
  )

  const reports = merchantObject

  const reportsArray: IReport[] = Object.keys(reports)
    .map((date: string) => {
      return {
        ...reports[date],
        date,
      }
    })
    .sort((a, b) => {
      const dateA: Date = new Date(a.date.split('/').reverse().join('-'))
      const dateB: Date = new Date(b.date.split('/').reverse().join('-'))
      return dateB.getTime() - dateA.getTime()
    })

  const totalSumUnit = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.unit) || 0),
    0,
  )
  const totalSumCodTotal = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.codTotal) || 0),
    0,
  )
  const totalSumCodCount = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.codCount) || 0),
    0,
  )
  const totalSumBankTotal = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.bankTotal) || 0),
    0,
  )
  const totalSumBankCount = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.bankCount) || 0),
    0,
  )
  const totalSumTax = reportsArray.reduce(
    (acc, item: IReport) =>
      acc +
      (Number(item.total) || 0) * ((merchant?.config?.taxPercent || 0) / 100),
    0,
  )

  const totalSumReturnPercent = reportsArray.reduce(
    (acc, item: IReport) =>
      acc + item.codTotal * ((merchant?.config?.returnPercent || 0) / 100),
    0,
  )
  const totalSumCostTotal = reportsArray.reduce(
    (acc, item: IReport) => acc + (Number(item.costTotal) || 0),
    0,
  )
  const totalSumCommission = reportsArray.reduce((acc, item: IReport) => {
    const comission = item?.[commissionTypeName as keyof IReport] ?? 0
    acc = acc + (Number(comission) || 0)
    return acc
  }, 0)
  const totalSumSubTotal = totalSumCodTotal + totalSumBankTotal

  const totalSumTaxAndReturnCost = totalSumTax + totalSumReturnPercent
  const totalSumCost = reportsArray.reduce((acc, item: IReport) => {
    const comission = item?.[commissionTypeName as keyof IReport] ?? 0
    let amount =
      acc +
      (Number(
        (item.costTotal || 0) +
        (expense === 1 ? item.costAmountTotal || 0 : 0) +
        (Number(comission) || 0),
      ) || 0)
    if (calTax === '1') {
      amount += totalSumTaxAndReturnCost
    }
    return amount
  }, 0)

  const columns = [
    {
      title: 'วันที่',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      align: 'center' as const,
      render: (date: string, report: IReport) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {date}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'จำนวนชิ้น',
      dataIndex: 'unit',
      key: 'unit',
      width: 75,
      align: 'center' as const,
      render: (unit: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {unit
            ? unit.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ยอด COD',
      dataIndex: 'codTotal',
      key: 'codTotal',
      width: 80,
      align: 'center' as const,
      render: (codTotal: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {codTotal
            ? codTotal.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวน COD',
      dataIndex: 'codCount',
      key: 'codCount',
      width: 95,
      align: 'center' as const,
      render: (codCount: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {codCount
            ? codCount.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ยอดโอน',
      dataIndex: 'bankTotal',
      key: 'bankTotal',
      width: 70,
      align: 'center' as const,
      render: (bankTotal: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {bankTotal
            ? bankTotal.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวนโอน',
      dataIndex: 'bankCount',
      key: 'bankCount',
      width: 85,
      align: 'center' as const,
      render: (bankCount: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {bankCount
            ? bankCount.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ต้นทุนสินค้า',
      dataIndex: 'costTotal',
      key: 'costTotal',
      width: 90,
      align: 'center' as const,
      render: (costTotal: number) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          textAlign={'center'}
        >
          {user?.isNotAllowSeeCost ? '-' : costTotal
            ? costTotal.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ค่าคอม',
      dataIndex: 'commission',
      key: 'commission',
      width: 70,
      align: 'center' as const,
      render: (commission: number, report: IReport) => {
        const comm = report?.[commissionTypeName as keyof IReport] ?? 0
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {comm
              ? comm?.toLocaleString('th-TH', { maximumFractionDigits: 2 })
              : 0}
          </Typography>
        )
      },
    },
  ]

  return (
    <>
      {isOpen && (
        <DailySalesReportOrderTableModal
          isOpen={isOpen}
          filter={filter}
          onClose={() => setIsOpen(false)}
          date={date}
        />
      )}
      {/* <CircularLoading isLoading={props.isLoading} /> */}
      <div ref={ref}>
        <Table
          loading={isLoading}
          size={'middle'}
          columns={columns}
          scroll={{ x: columns.reduce((pre, cur) => cur.width + pre, 0) }}
          dataSource={reportsArray.map((report: IReport, index: number) => ({
            ...report,
            index,
          }))}
          pagination={false}
          bordered
          summary={() => (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    รวม
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {totalSumUnit
                      ? totalSumUnit.toLocaleString('th-TH', {
                        maximumFractionDigits: 2,
                      })
                      : 0}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {totalSumCodTotal
                      ? totalSumCodTotal.toLocaleString('th-TH', {
                        maximumFractionDigits: 2,
                      })
                      : 0}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {totalSumCodCount
                      ? totalSumCodCount.toLocaleString('th-TH', {
                        maximumFractionDigits: 2,
                      })
                      : 0}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {totalSumBankTotal
                      ? totalSumBankTotal.toLocaleString('th-TH', {
                        maximumFractionDigits: 2,
                      })
                      : 0}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {totalSumBankCount
                      ? totalSumBankCount.toLocaleString('th-TH', {
                        maximumFractionDigits: 2,
                      })
                      : 0}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={0} colSpan={1}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {totalSumCostTotal
                      ? totalSumCostTotal.toLocaleString('th-TH', {
                        maximumFractionDigits: 2,
                      })
                      : 0}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={13}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {totalSumCommission
                      ? totalSumCommission.toLocaleString('th-TH', {
                        maximumFractionDigits: 2,
                      })
                      : 0}
                  </Typography>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          )}
        />
      </div>
    </>
  )
})

export default DailyTelesalesReportTable
